import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  AdminNewsletter,
  AdminNewsletterBanner,
  createNewsletterBanner,
  deleteNewsletter,
  deleteNewsletterBanner,
  getAdminNewsletters,
  getNewsletterBanners,
  getNewsletterFeatured,
  saveNewsletterFeatured,
  updateNewsletterBanner,
} from '../../../services/admin/newsletterApi';
import { AdminPage, AdminPageHeader, AdminPageTitle, AdminSection } from '../common/AdminPrimitives';
import { PrimaryButton } from '../common/AdminModalPrimitives';
import AdminFilterDropdown from '../common/AdminFilterDropdown';
import AdminSearchInput from '../common/AdminSearchInput';
import CommunityTablePagination from '../community/CommunityTablePagination';
import {
  ActionButton,
  ActionButtons,
  BodyCell,
  CellText,
  EmptyText,
  HeaderCell,
  HeaderText,
  TableBody,
  TableContainer,
  TableHeader,
  TableHeaderRow,
  TableInner,
  TableRow,
} from '../common/AdminTablePrimitives';
import {
  ActionRow,
  BannerPlusIcon,
  BannerThumbnail,
  ColorField,
  ColorSwatch,
  ColorText,
  ErrorMessage,
  FieldGroup,
  FieldLabel,
  FieldRow,
  IconButton,
  ImagePreview,
  InlineRow,
  limitLines,
  MinusIcon,
  PlusIcon,
  TextArea,
  TextInput,
  ThumbnailImage,
  UploadButton,
  UploadCaption,
  UploadRow,
} from './NewsletterStyles';
import NewsletterSelectModal from './NewsletterSelectModal';

// 피그마 컬럼 폭: 연번 100 / 제목 fill / 작성자 100 / 상태 100 / 등록일 150 / 관리 150
const TABLE_COLUMNS = '100px minmax(200px, 1fr) 100px 100px 150px 150px';
const MAX_BANNERS = 4;
const DEFAULT_FONT_COLOR = '#000000';

// 화면에서 편집 중인 배너 (서버 저장 전에는 id 가 temp- 로 시작합니다.)
interface LocalBanner {
  id: number | string;
  caption: string;
  title: string;
  fontColorHexCode: string;
  imageUrl: string;
  imageFile: File | null;
  isNew: boolean;
  isDirty: boolean;
}

interface LocalFeatured {
  caption: string;
  text: string;
  cardTitle: string;
  cardCaption: string;
  /** 카드에 연결된 뉴스레터 */
  newsletterId: number | null;
  newsletterTitle: string;
  imageUrl: string;
  imageFile: File | null;
  /** 서버에 저장된 이미지를 지우겠다고 표시한 상태 (저장 시 서버에 전달) */
  removeImage: boolean;
}

const createEmptyBanner = (): LocalBanner => ({
  id: `temp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  caption: '',
  title: '',
  fontColorHexCode: DEFAULT_FONT_COLOR,
  imageUrl: '',
  imageFile: null,
  isNew: true,
  isDirty: false,
});

const toLocalBanner = (banner: AdminNewsletterBanner): LocalBanner => ({
  id: banner.banner_id,
  caption: banner.caption ?? '',
  title: banner.title ?? '',
  fontColorHexCode: banner.font_color_hex_code || DEFAULT_FONT_COLOR,
  imageUrl: banner.banner_image_url ?? '',
  imageFile: null,
  isNew: false,
  isDirty: false,
});

const EMPTY_FEATURED: LocalFeatured = {
  caption: '',
  text: '',
  cardTitle: '',
  cardCaption: '',
  newsletterId: null,
  newsletterTitle: '',
  imageUrl: '',
  imageFile: null,
  removeImage: false,
};

const NewsletterManagementComponent: React.FC = () => {
  const navigate = useNavigate();

  const [banners, setBanners] = useState<LocalBanner[]>([createEmptyBanner()]);
  const [featured, setFeatured] = useState<LocalFeatured>(EMPTY_FEATURED);
  const [newsletters, setNewsletters] = useState<AdminNewsletter[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [pagination, setPagination] = useState({ page: 0, size: 10, totalPages: 0, totalElements: 0 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isNewsletterSelectOpen, setIsNewsletterSelectOpen] = useState(false);

  const bannerFileInputs = useRef<Record<string, HTMLInputElement | null>>({});
  const featuredFileInput = useRef<HTMLInputElement | null>(null);

  const fetchPageContent = useCallback(async () => {
    try {
      const [bannerList, featuredData] = await Promise.all([
        getNewsletterBanners(),
        getNewsletterFeatured(),
      ]);

      setBanners(bannerList.length > 0 ? bannerList.map(toLocalBanner) : [createEmptyBanner()]);
      setFeatured({
        caption: featuredData?.caption ?? '',
        text: featuredData?.text ?? '',
        cardTitle: featuredData?.card_title ?? '',
        cardCaption: featuredData?.card_caption ?? '',
        newsletterId: featuredData?.newsletter_id ?? null,
        newsletterTitle: featuredData?.newsletter_title ?? '',
        imageUrl: featuredData?.featured_image_url ?? '',
        imageFile: null,
        removeImage: false,
      });
    } catch (err: any) {
      setError(err.message || '뉴스레터 페이지 정보를 불러올 수 없습니다.');
    }
  }, []);

  const fetchNewsletters = useCallback(async () => {
    try {
      setLoading(true);
      const isTemp = statusFilter === '' ? undefined : statusFilter === 'true';
      const response = await getAdminNewsletters(
        pagination.page,
        pagination.size,
        isTemp,
        searchQuery
      );
      setNewsletters(response.content ?? []);
      setPagination((prev) => ({
        ...prev,
        totalPages: response.totalPages ?? 0,
        totalElements: response.totalElements ?? 0,
      }));
    } catch (err: any) {
      setError(err.message || '뉴스레터 목록을 불러올 수 없습니다.');
      setNewsletters([]);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.size, statusFilter, searchQuery]);

  useEffect(() => {
    fetchPageContent();
  }, [fetchPageContent]);

  useEffect(() => {
    fetchNewsletters();
  }, [fetchNewsletters]);

  // ------------------------------------------------------------------
  // 배너
  // ------------------------------------------------------------------

  const updateBannerField = (bannerId: number | string, patch: Partial<LocalBanner>) => {
    setBanners((prev) =>
      prev.map((banner) => (banner.id === bannerId ? { ...banner, ...patch, isDirty: true } : banner))
    );
  };

  const handleAddBanner = () => {
    setBanners((prev) => (prev.length >= MAX_BANNERS ? prev : [...prev, createEmptyBanner()]));
  };

  const handleRemoveBanner = async (bannerId: number | string) => {
    if (banners.length <= 1) return;

    const banner = banners.find((item) => item.id === bannerId);
    if (!banner) return;

    if (!banner.isNew && typeof bannerId === 'number') {
      if (!window.confirm('이 배너를 삭제하시겠습니까?')) return;
      try {
        await deleteNewsletterBanner(bannerId);
      } catch (err: any) {
        alert(err.message || '배너 삭제에 실패했습니다.');
        return;
      }
    }

    setBanners((prev) => prev.filter((item) => item.id !== bannerId));
  };

  const handleBannerImageChange = (bannerId: number | string, file: File | null) => {
    if (!file) return;
    updateBannerField(bannerId, { imageFile: file, imageUrl: URL.createObjectURL(file) });
  };

  // ------------------------------------------------------------------
  // 강조 섹션
  // ------------------------------------------------------------------

  const updateFeaturedField = (patch: Partial<LocalFeatured>) => {
    setFeatured((prev) => ({ ...prev, ...patch }));
  };

  const handleFeaturedImageChange = (file: File | null) => {
    if (!file) return;
    updateFeaturedField({ imageFile: file, imageUrl: URL.createObjectURL(file), removeImage: false });
  };

  const handleFeaturedImageRemove = () => {
    if (!window.confirm('강조 섹션 이미지를 삭제하시겠습니까? 저장하기를 눌러야 반영됩니다.')) return;
    // 파일 input 을 비워 같은 파일을 다시 선택해도 change 가 발생하도록 한다.
    if (featuredFileInput.current) featuredFileInput.current.value = '';
    updateFeaturedField({ imageFile: null, imageUrl: '', removeImage: true });
  };

  // ------------------------------------------------------------------
  // 저장 (배너 + 강조 섹션)
  // ------------------------------------------------------------------

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      for (const banner of banners) {
        const hasContent = banner.caption || banner.title || banner.imageFile || banner.imageUrl;
        if (!hasContent) continue;

        if (banner.isNew) {
          if (!banner.imageFile) {
            throw new Error('새로 추가한 배너의 이미지를 업로드해주세요.');
          }
          await createNewsletterBanner({
            caption: banner.caption,
            title: banner.title,
            fontColorHexCode: banner.fontColorHexCode,
            bannerImage: banner.imageFile,
          });
        } else if (banner.isDirty && typeof banner.id === 'number') {
          await updateNewsletterBanner(banner.id, {
            caption: banner.caption,
            title: banner.title,
            fontColorHexCode: banner.fontColorHexCode,
            bannerImage: banner.imageFile,
          });
        }
      }

      await saveNewsletterFeatured({
        caption: featured.caption,
        text: featured.text,
        cardTitle: featured.cardTitle,
        cardCaption: featured.cardCaption,
        newsletterId: featured.newsletterId,
        featuredImage: featured.imageFile,
        removeImage: featured.removeImage,
      });

      await fetchPageContent();
      alert('저장되었습니다.');
    } catch (err: any) {
      setError(err.message || '저장에 실패했습니다.');
      alert(err.message || '저장에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  // ------------------------------------------------------------------
  // 목록
  // ------------------------------------------------------------------

  const handleDeleteNewsletter = async (newsletterId: number) => {
    if (!window.confirm('이 뉴스레터를 삭제하시겠습니까?')) return;

    try {
      await deleteNewsletter(newsletterId);
      // 마지막 페이지의 마지막 항목을 지우면 빈 페이지가 남으므로 한 페이지 앞으로 이동합니다.
      if (newsletters.length === 1 && pagination.page > 0) {
        setPagination((prev) => ({ ...prev, page: prev.page - 1 }));
      } else {
        await fetchNewsletters();
      }
    } catch (err: any) {
      alert(err.message || '뉴스레터 삭제에 실패했습니다.');
    }
  };

  const formatDate = (value: string) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${date.getFullYear()}.${month}.${day}.`;
  };

  return (
    <AdminPage>
      <AdminPageHeader>
        <AdminPageTitle>뉴스레터 관리</AdminPageTitle>
      </AdminPageHeader>

      {error && <ErrorMessage>{error}</ErrorMessage>}

      {/* 배너 */}
      <AdminSection>
        <FieldGroup>
          <FieldLabel>배너</FieldLabel>
          {banners.map((banner) => (
            <BannerRow key={banner.id}>
              <BannerThumbnail
                type="button"
                onClick={() => bannerFileInputs.current[String(banner.id)]?.click()}
              >
                {banner.imageUrl ? (
                  <ThumbnailImage src={banner.imageUrl} alt="배너 이미지" />
                ) : (
                  <BannerPlusIcon />
                )}
              </BannerThumbnail>
              <HiddenFileInput
                ref={(el) => {
                  bannerFileInputs.current[String(banner.id)] = el;
                }}
                type="file"
                accept="image/png,image/jpeg"
                onChange={(e) => handleBannerImageChange(banner.id, e.target.files?.[0] ?? null)}
              />

              <BannerFields>
                <InlineRow>
                  <TextInput
                    value={banner.caption}
                    onChange={(e) => updateBannerField(banner.id, { caption: e.target.value })}
                    placeholder="상단 캡션을 입력해주세요. (최대 1줄)"
                  />
                  <IconButton
                    type="button"
                    onClick={handleAddBanner}
                    disabled={banners.length >= MAX_BANNERS}
                    aria-label="배너 추가"
                  >
                    <PlusIcon />
                  </IconButton>
                  <IconButton
                    type="button"
                    onClick={() => handleRemoveBanner(banner.id)}
                    disabled={banners.length <= 1}
                    aria-label="배너 삭제"
                  >
                    <MinusIcon />
                  </IconButton>
                </InlineRow>
                <TopAlignedInlineRow>
                  <TextArea
                    rows={3}
                    value={banner.title}
                    onChange={(e) => updateBannerField(banner.id, { title: limitLines(e.target.value, 3) })}
                    placeholder="제목을 입력해주세요. (최대 3줄)"
                  />
                  <ColorField>
                    <ColorSwatch
                      type="color"
                      value={banner.fontColorHexCode}
                      onChange={(e) => updateBannerField(banner.id, { fontColorHexCode: e.target.value })}
                      aria-label="배너 글자 색상"
                    />
                    <ColorText
                      type="text"
                      value={banner.fontColorHexCode}
                      onChange={(e) => {
                        const raw = e.target.value;
                        const next = raw && !raw.startsWith('#') ? `#${raw}` : raw;
                        if (next.length <= 7) {
                          updateBannerField(banner.id, { fontColorHexCode: next });
                        }
                      }}
                      placeholder={DEFAULT_FONT_COLOR}
                    />
                  </ColorField>
                </TopAlignedInlineRow>
              </BannerFields>
            </BannerRow>
          ))}
        </FieldGroup>
      </AdminSection>

      {/* 강조 섹션 */}
      <AdminSection>
        <FieldGroup>
          <FieldLabel>강조 섹션</FieldLabel>
          <TopAlignedFieldRow>
            <TextInput
              value={featured.caption}
              onChange={(e) => updateFeaturedField({ caption: e.target.value })}
              placeholder="상단 캡션을 입력해주세요. (최대 1줄)"
            />
            <TextArea
              rows={3}
              value={featured.text}
              onChange={(e) => updateFeaturedField({ text: limitLines(e.target.value, 3) })}
              placeholder="내용을 입력해주세요. (최대 3줄)"
            />
          </TopAlignedFieldRow>

          {/* 피그마: [이미지 업로드] 와 [뉴스레터 선택] 을 한 줄에 나란히 배치 (gap 8) */}
          <FeaturedActionsRow>
            <UploadRow>
              <UploadCaption>비율 21:9의 JPG, PNG 카드 이미지 파일 업로드</UploadCaption>
              <UploadActions>
                {featured.imageUrl && (
                  <RemoveImageButton type="button" onClick={handleFeaturedImageRemove}>
                    이미지 삭제
                  </RemoveImageButton>
                )}
                <UploadButton type="button" onClick={() => featuredFileInput.current?.click()}>
                  이미지 업로드
                </UploadButton>
              </UploadActions>
            </UploadRow>
            <UploadRow>
              <SelectedNewsletterCaption
                $selected={featured.newsletterId !== null}
                title={featured.newsletterTitle || undefined}
              >
                {featured.newsletterId !== null
                  ? featured.newsletterTitle || `뉴스레터 #${featured.newsletterId}`
                  : '연결될 뉴스레터 선택하기'}
              </SelectedNewsletterCaption>
              <UploadActions>
                {featured.newsletterId !== null && (
                  <RemoveImageButton
                    type="button"
                    onClick={() => updateFeaturedField({ newsletterId: null, newsletterTitle: '' })}
                  >
                    연결 해제
                  </RemoveImageButton>
                )}
                <UploadButton type="button" onClick={() => setIsNewsletterSelectOpen(true)}>
                  뉴스레터 선택
                </UploadButton>
              </UploadActions>
            </UploadRow>
          </FeaturedActionsRow>
          <HiddenFileInput
            ref={featuredFileInput}
            type="file"
            accept="image/png,image/jpeg"
            onChange={(e) => handleFeaturedImageChange(e.target.files?.[0] ?? null)}
          />
          {featured.imageUrl ? (
            <ImagePreview $ratio="21 / 9">
              <img src={featured.imageUrl} alt="강조 섹션 이미지" />
            </ImagePreview>
          ) : featured.removeImage ? (
            <RemovedNotice>저장하기를 누르면 기존 이미지가 삭제됩니다.</RemovedNotice>
          ) : null}

          <TopAlignedFieldRow>
            <TextArea
              rows={2}
              value={featured.cardTitle}
              onChange={(e) => updateFeaturedField({ cardTitle: limitLines(e.target.value, 2) })}
              placeholder="카드 제목을 입력해주세요. (최대 2줄)"
            />
            <TextInput
              value={featured.cardCaption}
              onChange={(e) => updateFeaturedField({ cardCaption: e.target.value })}
              placeholder="카드 캡션을 입력해주세요. (최대 1줄)"
            />
          </TopAlignedFieldRow>
        </FieldGroup>
      </AdminSection>

      {/* 뉴스레터 목록 */}
      <AdminSection>
        <FieldGroup>
          <FieldLabel>뉴스레터 목록</FieldLabel>
          <FilterRow>
            <AdminFilterDropdown
              value={statusFilter}
              onChange={(value) => {
                setStatusFilter(value);
                setPagination((prev) => ({ ...prev, page: 0 }));
              }}
              maxWidth="140px"
              options={[
                { value: '', label: '상태' },
                { value: 'false', label: '등록 완료' },
                { value: 'true', label: '임시 저장' },
              ]}
            />
            <AdminSearchInput
              value={searchQuery}
              onChange={(value) => {
                setSearchQuery(value);
                setPagination((prev) => ({ ...prev, page: 0 }));
              }}
              placeholder="제목 또는 작성자를 입력해주세요."
            />
          </FilterRow>
          <TableContainer>
            <TableInner>
              <TableHeader>
                <TableHeaderRow $columns={TABLE_COLUMNS}>
                  <HeaderCell><HeaderText>연번</HeaderText></HeaderCell>
                  <HeaderCell><HeaderText>제목</HeaderText></HeaderCell>
                  <HeaderCell><HeaderText>작성자</HeaderText></HeaderCell>
                  <HeaderCell><HeaderText>상태</HeaderText></HeaderCell>
                  <HeaderCell><HeaderText>등록일</HeaderText></HeaderCell>
                  <HeaderCell><HeaderText>관리</HeaderText></HeaderCell>
                </TableHeaderRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <StateRow><EmptyText>불러오는 중...</EmptyText></StateRow>
                ) : newsletters.length > 0 ? (
                  newsletters.map((newsletter, index) => (
                    <TableRow key={newsletter.newsletter_id} $columns={TABLE_COLUMNS}>
                      <BodyCell>
                        <CellText>
                          {pagination.totalElements - pagination.page * pagination.size - index}
                        </CellText>
                      </BodyCell>
                      <BodyCell>
                        <EllipsisText title={newsletter.title}>{newsletter.title}</EllipsisText>
                      </BodyCell>
                      <BodyCell><CellText>{newsletter.author || '-'}</CellText></BodyCell>
                      <BodyCell>
                        <StatusText $published={!newsletter.is_temp}>
                          {newsletter.is_temp ? '임시 저장' : '등록 완료'}
                        </StatusText>
                      </BodyCell>
                      <BodyCell><CellText>{formatDate(newsletter.created_at)}</CellText></BodyCell>
                      <BodyCell>
                        <ActionButtons>
                          <EditButton
                            $tone="primary"
                            onClick={() => navigate(`/admin/newsletter/register?newsletterId=${newsletter.newsletter_id}`)}
                          >
                            수정
                          </EditButton>
                          <ActionButton
                            $tone="danger"
                            onClick={() => handleDeleteNewsletter(newsletter.newsletter_id)}
                          >
                            삭제
                          </ActionButton>
                        </ActionButtons>
                      </BodyCell>
                    </TableRow>
                  ))
                ) : (
                  <StateRow><EmptyText>등록된 뉴스레터가 없습니다.</EmptyText></StateRow>
                )}
              </TableBody>
            </TableInner>
            <CommunityTablePagination
              currentPage={pagination.page + 1}
              totalPages={pagination.totalPages}
              onPageChange={(page) => setPagination((prev) => ({ ...prev, page: page - 1 }))}
            />
          </TableContainer>
        </FieldGroup>
      </AdminSection>

      <ActionRow>
        <PrimaryButton type="button" onClick={handleSave} disabled={saving}>
          {saving ? '저장 중...' : '저장하기'}
        </PrimaryButton>
      </ActionRow>

      {isNewsletterSelectOpen && (
        <NewsletterSelectModal
          selectedId={featured.newsletterId}
          onClose={() => setIsNewsletterSelectOpen(false)}
          onSelect={(newsletter) => {
            updateFeaturedField({ newsletterId: newsletter.id, newsletterTitle: newsletter.title });
            setIsNewsletterSelectOpen(false);
          }}
        />
      )}
    </AdminPage>
  );
};

const BannerRow = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  gap: 24px;
`;

const BannerFields = styled.div`
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;
  justify-content: center;
  gap: 16px;
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const FilterRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const FeaturedActionsRow = styled.div`
  display: flex;
  width: 100%;
  gap: 8px;

  & > * {
    flex: 1;
    min-width: 0;
  }

  @media (max-width: 1024px) {
    flex-direction: column;
  }
`;

const SelectedNewsletterCaption = styled(UploadCaption)<{ $selected: boolean }>`
  overflow: hidden;
  color: ${({ $selected }) => ($selected ? '#171719' : 'rgba(55, 56, 60, 0.61)')};
  font-weight: ${({ $selected }) => ($selected ? 600 : 400)};
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const UploadActions = styled.div`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: 8px;
`;

const RemoveImageButton = styled.button`
  display: flex;
  height: 48px;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(112, 115, 124, 0.22);
  border-radius: 12px;
  padding: 0 20px;
  background: #fff;
  color: #ec4e4e;
  font-family: inherit;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: rgba(236, 78, 78, 0.06);
  }
`;

const RemovedNotice = styled.div`
  color: #ec4e4e;
  font-size: 14px;
`;

const StateRow = styled.div`
  display: flex;
  justify-content: center;
  padding: 48px 20px;
`;

const EllipsisText = styled.span`
  display: block;
  overflow: hidden;
  color: #171719;
  font-size: 16px;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StatusText = styled.span<{ $published: boolean }>`
  color: ${({ $published }) => ($published ? '#296768' : 'rgba(55, 56, 60, 0.61)')};
  font-size: 16px;
  white-space: nowrap;
`;

const EditButton = styled(ActionButton)`
  color: #171719;

  &:hover {
    background-color: rgba(23, 23, 25, 0.05);
  }
`;

export default NewsletterManagementComponent;

// 여러 줄 입력(TextArea) 옆의 한 줄 입력이 윗줄에 맞춰 정렬되도록
const TopAlignedInlineRow = styled(InlineRow)`
  align-items: flex-start;
`;

const TopAlignedFieldRow = styled(FieldRow)`
  align-items: flex-start;
`;
